import { useMemo } from 'react';

import {
  useGetSharedLineItemDeliveryLocationQuery,
  useGetSharedLineItemRestaurantQuery,
} from '../../GraphQL/ShareLineItemScreen.generated';

// ─────────────────────────────────────────────────────────────────────────────

export const useShareLineItemLocationDetails = (params: Params) => {
  const { deliveryAddressId, restaurantId } = params;

  const hasDeliveryAddress = deliveryAddressId !== undefined;

  // ─── Remote Data ─────────────────────────────────────────────────────

  const [restaurantResponse] = useGetSharedLineItemRestaurantQuery({
    variables: { id: restaurantId ?? '' },
    pause: !restaurantId || hasDeliveryAddress,
    requestPolicy: 'network-only',
  });

  const [deliveryLocationResponse] = useGetSharedLineItemDeliveryLocationQuery({
    variables: {
      addressId: deliveryAddressId ?? '',
      input: { addressId: deliveryAddressId ?? '' },
    },
    pause: !hasDeliveryAddress,
    requestPolicy: 'network-only',
  });

  // ─── Derived ─────────────────────────────────────────────────────────

  const isFetching = restaurantResponse.fetching || deliveryLocationResponse.fetching;

  const restaurant = restaurantResponse.data?.restaurant;
  const deliveryLocationData = deliveryLocationResponse.data;
  const deliveryAddress = deliveryLocationData?.address;

  const hasDeliveryDetails = deliveryLocationData?.locationByAddressId?.__typename === 'Location';
  const hasDeliveryInfo = hasDeliveryAddress && hasDeliveryDetails && deliveryAddress;
  const hasRestaurantInfo = restaurantId && restaurant;

  // ─────────────────────────────────────────────────────────────────────

  return useMemo(() => {
    if (isFetching) {
      return { isFetching: true };
    }

    // ─── Delivery ──────────────────────────────────────────────────────

    if (hasDeliveryInfo) {
      const orderChannel: OrderChannel = 'delivery';

      const deliveryLocation = deliveryLocationData.locationByAddressId;
      const deliveryRestaurant = deliveryLocation.restaurant;
      const completeAddress = formatAddress(deliveryAddress);

      return {
        isFetching: false,
        orderChannel,
        location: {
          label: deliveryAddress.name,
          restaurant: deliveryRestaurant,
          address: completeAddress,
          secondaryAddress: formatSecondaryAddress(deliveryAddress),
        },
        deliveryOrderDetail: {
          addressId: deliveryAddressId,
          addressName: deliveryAddress.name,
          completeAddress,
          deliveryFee: deliveryLocation.deliveryFee,
          tip: 0,
          vendor: deliveryLocation.vendor.name,
          vendorRestaurantId: deliveryLocation.vendor.restaurantId,
        },
      };
    }

    // ─── Pickup Or Outpost ─────────────────────────────────────────────

    if (hasRestaurantInfo) {
      const orderChannel: OrderChannel = restaurant.isOutpost ? 'outpost' : 'pickup';

      return {
        isFetching: false,
        orderChannel,
        location: {
          label: restaurant.name,
          restaurant,
          address: restaurant.address,
          secondaryAddress: formatSecondaryAddress(restaurant),
        },
      };
    }

    return { isFetching: false };
  }, [
    deliveryAddress,
    deliveryAddressId,
    deliveryLocationData,
    hasDeliveryInfo,
    hasRestaurantInfo,
    isFetching,
    restaurant,
  ]);
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatAddress(address: MaybeAddress) {
  return [address?.street, address?.secondaryStreet].filter(Boolean).join(', ');
}

function formatSecondaryAddress(address: MaybeSecondaryAddress) {
  const stateAndZip = [address?.state, address?.zipCode].filter(Boolean).join(' ');

  return [address?.city, stateAndZip].filter(Boolean).join(', ');
}

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  restaurantId?: string;
  deliveryAddressId?: string;
};

type MaybeAddress = { street?: string | null; secondaryStreet?: string | null } | undefined | null;
type MaybeSecondaryAddress =
  | { city?: string | null; state?: string | null; zipCode?: string | null }
  | undefined
  | null;

type OrderChannel = 'pickup' | 'delivery' | 'outpost';
